'use client'

import * as React from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { useLocale } from 'next-intl'

import { localeHref } from '../lib/locale-href'
import { applyStudioLandingPagesNavigate } from './next-routing-nav'
import type { NavigateToFn, StudioLayoutLinkProps } from '@repo/studio-config/studio-config-context'

export function useNextCurrentPathname(): string {
  return usePathname() ?? '/'
}

export function useNextCurrentLocationState(): unknown {
  return undefined
}

/**
 * In-MFE navigations stay exact: this adapter also backs
 * `useStudioNavigateToExact`. Prefixing those would put `/fr` back when
 * switching to English. Document loads to the Vite SPA prefix in
 * `toDocumentHref`, same as `NextLinkComponent`.
 */
export function useNextNavigateTo(): NavigateToFn {
  const router = useRouter()
  const locale = useLocale()
  return React.useCallback<NavigateToFn>(
    (path, options) => {
      applyStudioLandingPagesNavigate(path, options, {
        assignDocument: href => window.location.assign(href),
        replaceDocument: href => window.location.replace(href),
        push: href => router.push(href),
        replace: href => router.replace(href),
        toDocumentHref: href => localeHref(href, locale),
      })
    },
    [locale, router],
  )
}

/**
 * Studio chrome targets the Vite SPA (home, profile, tools). This MFE only
 * owns `/platform`, `/desktop`, and `/moments`, so chrome links are real `<a>`s —
 * `next/link` would try to client-route into the other microfrontend.
 */
export function NextLinkComponent({
  to,
  reloadDocument: _reloadDocument,
  mask: _mask,
  ...props
}: StudioLayoutLinkProps) {
  const locale = useLocale()
  const href = localeHref(to, locale)

  return <a href={href} {...props} />
}
